import { Link } from 'react-router-dom'
import { useState } from 'react'
import { Search, ChevronDown, MessageCircle, Phone, FileText, HelpCircle } from 'lucide-react'
import type { FAQ } from '../../types/support'

const FAQS: FAQ[] = [
  {
    id: '1',
    category: 'Orders',
    question: 'How do I track my order?',
    answer: 'You can track your order by going to "My Orders" section in your account. Each order has a detailed tracking timeline showing the current status.',
    helpful_count: 45,
  },
  {
    id: '2',
    category: 'Orders',
    question: 'Can I cancel my order?',
    answer: 'Yes, you can cancel your order before it is shipped. Go to your order details and click "Cancel Order". Refunds will be processed within 5-7 business days.',
    helpful_count: 38,
  },
  {
    id: '3',
    category: 'Delivery',
    question: 'What are the delivery charges?',
    answer: 'Free delivery on orders above ৳500. For orders below ৳500, a delivery charge of ৳50 applies.',
    helpful_count: 67,
  },
  {
    id: '4',
    category: 'Delivery',
    question: 'What are the delivery time slots?',
    answer: 'We offer multiple delivery slots: Morning (9-11 AM), Afternoon (2-4 PM), and Evening (5-7 PM). You can choose your preferred slot during checkout.',
    helpful_count: 52,
  },
  {
    id: '5',
    category: 'Payment',
    question: 'What payment methods do you accept?',
    answer: 'We accept credit/debit cards, bKash, Nagad, Rocket, Upay, and Cash on Delivery.',
    helpful_count: 41,
  },
  {
    id: '6',
    category: 'Payment',
    question: 'Is my payment information secure?',
    answer: 'Yes, absolutely. We use industry-standard encryption and never store your card details on our servers.',
    helpful_count: 33,
  },
  {
    id: '7',
    category: 'Returns',
    question: 'What is your return policy?',
    answer: 'We accept returns within 24 hours of delivery for perishable items and 7 days for non-perishable items, provided they are in original condition.',
    helpful_count: 29,
  },
  {
    id: '8',
    category: 'Account',
    question: 'How do I reset my password?',
    answer: 'Click on "Forgot Password" on the login page. Enter your email address and we will send you a password reset link.',
    helpful_count: 24,
  },
]

export default function SupportPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<string>('All')

  const categories = ['All', ...Array.from(new Set(FAQS.map((faq) => faq.category)))]

  const filteredFAQs = FAQS.filter((faq) => {
    const matchesSearch =
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesCategory = selectedCategory === 'All' || faq.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  return (
    <>
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold mb-4">How can we help you?</h1>
        <p className="text-gray-600 mb-8">Find answers to your questions or get in touch with our support team</p>

        {/* Search */}
        <div className="max-w-2xl mx-auto relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search for help..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg"
          />
        </div>
      </div>

      {/* Contact Options */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        <Link
          to="/support/chat"
          className="bg-white border border-gray-200 rounded-lg p-6 hover:border-blue-500 hover:shadow-md transition-all"
        >
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <MessageCircle className="w-6 h-6 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold mb-2">Live Chat</h3>
          <p className="text-sm text-gray-600">Chat with our support team in real-time</p>
        </Link>

        <Link
          to="/support/tickets/new"
          className="bg-white border border-gray-200 rounded-lg p-6 hover:border-blue-500 hover:shadow-md transition-all"
        >
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <FileText className="w-6 h-6 text-green-600" />
          </div>
          <h3 className="text-lg font-semibold mb-2">Submit a Ticket</h3>
          <p className="text-sm text-gray-600">Create a support ticket for detailed issues</p>
        </Link>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
            <Phone className="w-6 h-6 text-purple-600" />
          </div>
          <h3 className="text-lg font-semibold mb-2">Call Us</h3>
          <p className="text-sm text-gray-600 mb-2">Hotline: +880 1234-567890</p>
          <p className="text-xs text-gray-500">Mon-Sat: 9 AM - 9 PM</p>
        </div>
      </div>

      {/* FAQs */}
      <div>
        <h2 className="text-2xl font-bold mb-6">Frequently Asked Questions</h2>

        {/* Category Filter */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                selectedCategory === category
                  ? 'bg-blue-100 text-blue-700 border border-blue-300'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* FAQ List */}
        <div className="space-y-3">
          {filteredFAQs.length === 0 ? (
            <div className="text-center py-12">
              <HelpCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">No FAQs found. Try a different search or category.</p>
            </div>
          ) : (
            filteredFAQs.map((faq) => (
              <div key={faq.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setExpandedId(expandedId === faq.id ? null : faq.id)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3 text-left">
                    <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {faq.category}
                    </span>
                    <span className="font-medium">{faq.question}</span>
                  </div>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${
                      expandedId === faq.id ? 'transform rotate-180' : ''
                    }`}
                  />
                </button>
                {expandedId === faq.id && (
                  <div className="px-4 pb-4 border-t border-gray-100">
                    <p className="text-gray-700 mt-3">{faq.answer}</p>
                    <div className="mt-4 flex items-center gap-4 text-sm text-gray-500">
                      <span>{faq.helpful_count} people found this helpful</span>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Still Need Help */}
      <div className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-8 text-center">
        <h3 className="text-xl font-semibold mb-2">Still need help?</h3>
        <p className="text-gray-600 mb-6">Our support team is here to assist you</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/support/chat"
            className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Start Live Chat
          </Link>
          <Link
            to="/support/tickets/new"
            className="inline-block px-6 py-3 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
          >
            Submit a Ticket
          </Link>
        </div>
      </div>
    </>
  )
}
